import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { SavedPaymentMethodResponse } from "../../types/generated";

const STORAGE_PREFIX = "bw:payment-method-detached:v1:";
const UNCERTAIN_STORAGE_PREFIX = "bw:payment-method-uncertain:v1:";
const ALIAS_STORAGE_PREFIX = "bw:payment-method-wallet-alias:v1:";
const OWNER_STORAGE_KEY = "bw:payment-method-detached-owner:v1";
const PLATFORM_WALLET_ID = "platform";
const memoryFallback = new Map<string, Set<string>>();
const uncertainMemoryFallback = new Map<string, Set<string>>();
const queryWalletAliases = new Map<string, Set<string>>();
const walletQueryKeys = new Map<string, Map<string, QueryKey>>();
let memoryOwner: string | null | undefined;
let authBoundaryGeneration = 0;

export class PaymentMethodAuthBoundaryError extends Error {
  constructor() {
    super("The authenticated payment-method context changed.");
    this.name = "PaymentMethodAuthBoundaryError";
  }
}

export class PaymentMethodRemovalInProgressError extends Error {
  constructor() {
    super("A saved-card removal is already in progress for this wallet.");
    this.name = "PaymentMethodRemovalInProgressError";
  }
}

export class PaymentMethodRemovalPersistenceError extends Error {
  constructor() {
    super(
      "Saved-card removal could not be persisted in browser storage. No removal request was sent.",
    );
    this.name = "PaymentMethodRemovalPersistenceError";
  }
}

export class PaymentMethodRemovalReferenceError extends Error {
  constructor() {
    super("The saved-card removal reference is unavailable.");
    this.name = "PaymentMethodRemovalReferenceError";
  }
}

export interface PaymentMethodRemovalOperation {
  readonly barrierScope: string;
  readonly connectedAccountId: string;
  readonly generation: number;
  readonly paymentMethodId: string;
  readonly paymentMethodsQueryKey: QueryKey;
  readonly queryScope: string;
}

export function paymentMethodIdentityKey(
  method: Pick<
    SavedPaymentMethodResponse,
    "connect_account_id" | "payment_method_id"
  >,
): string {
  return JSON.stringify([method.connect_account_id, method.payment_method_id]);
}

export function isSamePaymentMethodIdentity(
  left: Pick<
    SavedPaymentMethodResponse,
    "connect_account_id" | "payment_method_id"
  >,
  right: Pick<
    SavedPaymentMethodResponse,
    "connect_account_id" | "payment_method_id"
  >,
): boolean {
  return paymentMethodIdentityKey(left) === paymentMethodIdentityKey(right);
}

export function capturePaymentMethodDetachBarrierGeneration(): number {
  return authBoundaryGeneration;
}

export function isPaymentMethodDetachBarrierGenerationCurrent(
  generation: number,
): boolean {
  return generation === authBoundaryGeneration;
}

export function assertPaymentMethodDetachBarrierGenerationCurrent(
  generation: number,
): void {
  if (!isPaymentMethodDetachBarrierGenerationCurrent(generation)) {
    throw new PaymentMethodAuthBoundaryError();
  }
}

function advanceAuthBoundaryGeneration(): void {
  authBoundaryGeneration += 1;
}

export function paymentMethodQueryScope(queryKey: QueryKey): string {
  return JSON.stringify(queryKey);
}

function queryBarrierScope(queryKey: QueryKey): string {
  return `query:${paymentMethodQueryScope(queryKey)}`;
}

function walletBarrierScope(connectedAccountId: string): string {
  return `wallet:${connectedAccountId}`;
}

type BarrierKind = "detached" | "uncertain";

function storageKey(
  barrierScope: string,
  kind: BarrierKind = "detached",
): string {
  const prefix =
    kind === "detached" ? STORAGE_PREFIX : UNCERTAIN_STORAGE_PREFIX;
  return `${prefix}${encodeURIComponent(barrierScope)}`;
}

function aliasStorageKey(queryScope: string): string {
  return `${ALIAS_STORAGE_PREFIX}${encodeURIComponent(queryScope)}`;
}

function browserStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function fallbackFor(kind: BarrierKind): Map<string, Set<string>> {
  return kind === "detached" ? memoryFallback : uncertainMemoryFallback;
}

function requireReadableOwnedStorage(): Storage {
  const storage = browserStorage();
  if (
    storage === null ||
    typeof memoryOwner !== "string" ||
    memoryOwner.length === 0
  ) {
    throw new PaymentMethodRemovalPersistenceError();
  }

  let persistedOwner: string | null;
  try {
    persistedOwner = storage.getItem(OWNER_STORAGE_KEY);
  } catch {
    throw new PaymentMethodRemovalPersistenceError();
  }
  if (persistedOwner !== memoryOwner) {
    throw new PaymentMethodRemovalPersistenceError();
  }
  return storage;
}

function readPersistedIds(
  barrierScope: string,
  kind: BarrierKind = "detached",
): Set<string> {
  const fallback = fallbackFor(kind);
  const ids = new Set(fallback.get(barrierScope) ?? []);
  const storage = requireReadableOwnedStorage();
  let raw: string | null;
  try {
    raw = storage.getItem(storageKey(barrierScope, kind));
  } catch {
    throw new PaymentMethodRemovalPersistenceError();
  }
  if (raw === null) return ids;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new PaymentMethodRemovalPersistenceError();
  }
  if (
    !Array.isArray(parsed) ||
    parsed.some((value) => typeof value !== "string" || value.length === 0)
  ) {
    throw new PaymentMethodRemovalPersistenceError();
  }
  for (const paymentMethodId of parsed) ids.add(paymentMethodId);
  return ids;
}

function persistIds(
  barrierScope: string,
  ids: Set<string>,
  kind: BarrierKind = "detached",
): boolean {
  const storage = browserStorage();
  if (storage !== null) {
    const key = storageKey(barrierScope, kind);
    try {
      if (ids.size === 0) {
        storage.removeItem(key);
      } else {
        storage.setItem(key, JSON.stringify([...ids]));
      }
    } catch {
      // Verify below: a throwing storage shim may still have committed.
    }
    try {
      const raw = storage.getItem(key);
      const parsed: unknown = raw === null ? [] : JSON.parse(raw);
      if (
        Array.isArray(parsed) &&
        parsed.every((value) => typeof value === "string" && value.length > 0)
      ) {
        const persisted = new Set(parsed);
        const matches =
          persisted.size === ids.size &&
          [...ids].every((paymentMethodId) => persisted.has(paymentMethodId));
        fallbackFor(kind).set(barrierScope, new Set(ids));
        return matches;
      }
    } catch {
      // The in-memory fallback still protects the current document.
    }
  }
  fallbackFor(kind).set(barrierScope, new Set(ids));
  return false;
}

function hasDurableOwner(): boolean {
  if (typeof memoryOwner !== "string" || memoryOwner.length === 0) return false;
  try {
    return browserStorage()?.getItem(OWNER_STORAGE_KEY) === memoryOwner;
  } catch {
    return false;
  }
}

function clearInMemoryDetachBarriers(): void {
  memoryFallback.clear();
  uncertainMemoryFallback.clear();
  queryWalletAliases.clear();
  walletQueryKeys.clear();
}

function isPersistedDetachBarrierKey(key: string | null): key is string {
  return Boolean(
    key?.startsWith(STORAGE_PREFIX) ||
      key?.startsWith(UNCERTAIN_STORAGE_PREFIX) ||
      key?.startsWith(ALIAS_STORAGE_PREFIX),
  );
}

function persistedDetachBarrierKeys(storage: Storage): string[] | null {
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (isPersistedDetachBarrierKey(key)) keys.push(key);
    }
    return keys;
  } catch {
    return null;
  }
}

function clearPersistedIds(storage: Storage): boolean {
  const keys = persistedDetachBarrierKeys(storage);
  if (keys === null) return false;
  for (const key of keys) {
    try {
      storage.removeItem(key);
    } catch {
      // Verify the final storage state below: a throwing shim may still commit.
    }
  }
  return persistedDetachBarrierKeys(storage)?.length === 0;
}

export function clearPaymentMethodDetachBarriers(): void {
  const storage = browserStorage();
  clearInMemoryDetachBarriers();
  if (storage !== null) clearPersistedIds(storage);
  memoryOwner = undefined;
  advanceAuthBoundaryGeneration();
  try {
    storage?.removeItem(OWNER_STORAGE_KEY);
  } catch {
    // An unavailable sessionStorage has no persisted owner to clear.
  }
}

/**
 * A tab may outlive multiple authenticated users. Keep reload protection for
 * the same user, but never carry Stripe identifiers across an identity change.
 */
export function synchronizePaymentMethodDetachBarrierOwner(
  userId: string | null,
): void {
  const storage = browserStorage();
  let persistedOwner: string | null;
  try {
    if (storage === null) throw new PaymentMethodRemovalPersistenceError();
    persistedOwner = storage.getItem(OWNER_STORAGE_KEY);
  } catch {
    // Never mutate durable fences when their owner cannot first be verified.
    // Keep the current document isolated if its in-memory identity changed.
    if (
      userId === null ||
      (memoryOwner !== undefined && memoryOwner !== userId)
    ) {
      clearInMemoryDetachBarriers();
      advanceAuthBoundaryGeneration();
    }
    memoryOwner = userId;
    return;
  }

  const memoryOwnerChanged =
    memoryOwner !== undefined && memoryOwner !== userId;
  if (userId === null || persistedOwner !== userId || memoryOwnerChanged) {
    clearInMemoryDetachBarriers();
    const persistedBarriersCleared = clearPersistedIds(storage);
    advanceAuthBoundaryGeneration();
    memoryOwner = userId;
    if (!persistedBarriersCleared) return;
  } else {
    memoryOwner = userId;
  }

  try {
    if (userId === null) {
      storage?.removeItem(OWNER_STORAGE_KEY);
    } else {
      storage?.setItem(OWNER_STORAGE_KEY, userId);
    }
  } catch {
    // The in-memory owner still isolates this document.
  }
}

function registerWalletAlias(
  queryClient: QueryClient,
  paymentMethodsQueryKey: QueryKey,
  connectedAccountId: string,
  generation: number,
  migrateQueryLocalBarrier = true,
  retainExistingAliases = false,
): string {
  assertPaymentMethodDetachBarrierGenerationCurrent(generation);
  const queryScope = paymentMethodQueryScope(paymentMethodsQueryKey);
  const barrierScope = walletBarrierScope(connectedAccountId);
  if (migrateQueryLocalBarrier) {
    for (const kind of ["detached", "uncertain"] as const) {
      migrateBarrierIds(
        queryClient,
        queryBarrierScope(paymentMethodsQueryKey),
        barrierScope,
        kind,
      );
    }
  }
  const previousScopes = readWalletAliases(paymentMethodsQueryKey);
  const nextScopes = retainExistingAliases
    ? new Set([...previousScopes, barrierScope])
    : new Set([barrierScope]);
  for (const previousScope of previousScopes) {
    if (!nextScopes.has(previousScope)) {
      walletQueryKeys.get(previousScope)?.delete(queryScope);
    }
  }
  queryWalletAliases.set(queryScope, nextScopes);
  for (const nextScope of nextScopes) {
    registerWalletQueryKey(nextScope, queryScope, paymentMethodsQueryKey);
  }
  persistWalletAliases(queryScope, nextScopes);
  return barrierScope;
}

function registerWalletQueryKey(
  barrierScope: string,
  queryScope: string,
  paymentMethodsQueryKey: QueryKey,
): void {
  const aliases =
    walletQueryKeys.get(barrierScope) ?? new Map<string, QueryKey>();
  aliases.set(queryScope, paymentMethodsQueryKey);
  walletQueryKeys.set(barrierScope, aliases);
}

function persistWalletAliases(
  queryScope: string,
  barrierScopes: ReadonlySet<string>,
): boolean {
  const expectedAccountIds = new Set(
    [...barrierScopes].map((scope) => scope.slice("wallet:".length)),
  );
  try {
    const storage = browserStorage();
    if (storage === null) return false;
    storage.setItem(
      aliasStorageKey(queryScope),
      JSON.stringify([...expectedAccountIds]),
    );
  } catch {
    // Verify below: a throwing storage shim may still have committed.
  }
  try {
    const raw = browserStorage()?.getItem(aliasStorageKey(queryScope));
    if (!raw) return false;
    let accountIds: string[];
    try {
      const parsed: unknown = JSON.parse(raw);
      accountIds = Array.isArray(parsed)
        ? parsed.filter(
            (value): value is string =>
              typeof value === "string" && value.length > 0,
          )
        : [];
    } catch {
      accountIds = [raw];
    }
    const persistedAccountIds = new Set(accountIds);
    return (
      persistedAccountIds.size === expectedAccountIds.size &&
      [...expectedAccountIds].every((accountId) =>
        persistedAccountIds.has(accountId),
      )
    );
  } catch {
    return false;
  }
}

function hasDurableWalletAliases(paymentMethodsQueryKey: QueryKey): boolean {
  const queryScope = paymentMethodQueryScope(paymentMethodsQueryKey);
  const expected = queryWalletAliases.get(queryScope);
  return expected !== undefined && persistWalletAliases(queryScope, expected);
}

function registerWalletAliases(
  paymentMethodsQueryKey: QueryKey,
  connectedAccountIds: ReadonlySet<string>,
  generation: number,
): void {
  assertPaymentMethodDetachBarrierGenerationCurrent(generation);
  const queryScope = paymentMethodQueryScope(paymentMethodsQueryKey);
  const barrierScopes = new Set(
    [...connectedAccountIds].map(walletBarrierScope),
  );
  for (const previousScope of readWalletAliases(paymentMethodsQueryKey)) {
    if (!barrierScopes.has(previousScope)) {
      walletQueryKeys.get(previousScope)?.delete(queryScope);
    }
  }
  queryWalletAliases.set(queryScope, barrierScopes);
  for (const barrierScope of barrierScopes) {
    registerWalletQueryKey(barrierScope, queryScope, paymentMethodsQueryKey);
  }
  persistWalletAliases(queryScope, barrierScopes);
}

function connectedAccountIdFromMethods(
  methods: readonly SavedPaymentMethodResponse[],
): string | null {
  const connectedAccountIds = connectedAccountIdsFromMethods(methods);
  if (connectedAccountIds.size === 1) return [...connectedAccountIds][0];
  // The unified vault deliberately has no Connect account on its public card
  // rows. It is still one durable wallet, not an unscoped list that cannot be
  // fenced during a removal.
  if (
    methods.length > 0 &&
    methods.every((method) => method.connect_account_id == null)
  ) {
    return PLATFORM_WALLET_ID;
  }
  return null;
}

function connectedAccountIdsFromMethods(
  methods: readonly SavedPaymentMethodResponse[],
): Set<string> {
  return new Set(
    methods
      .map((method) => method.connect_account_id)
      .filter(
        (connectedAccountId): connectedAccountId is string =>
          typeof connectedAccountId === "string" &&
          connectedAccountId.length > 0,
      ),
  );
}

function readWalletAliases(paymentMethodsQueryKey: QueryKey): Set<string> {
  const queryScope = paymentMethodQueryScope(paymentMethodsQueryKey);
  const known = queryWalletAliases.get(queryScope);
  if (known !== undefined) return new Set(known);
  try {
    const raw = browserStorage()?.getItem(aliasStorageKey(queryScope));
    if (!raw) return new Set();
    let connectedAccountIds: string[];
    try {
      const parsed: unknown = JSON.parse(raw);
      connectedAccountIds = Array.isArray(parsed)
        ? parsed.filter(
            (value): value is string =>
              typeof value === "string" && value.length > 0,
          )
        : [];
    } catch {
      // Compatibility with the original v1 format, which stored one raw id.
      connectedAccountIds = [raw];
    }
    const barrierScopes = new Set(connectedAccountIds.map(walletBarrierScope));
    queryWalletAliases.set(queryScope, barrierScopes);
    return new Set(barrierScopes);
  } catch {
    // No persisted alias is available; use the query-local fallback.
  }
  return new Set();
}

function readWalletAlias(paymentMethodsQueryKey: QueryKey): string | null {
  const aliases = readWalletAliases(paymentMethodsQueryKey);
  return aliases.size === 1 ? [...aliases][0] : null;
}

function registerPaymentMethodWalletAlias(
  queryClient: QueryClient,
  paymentMethodsQueryKey: QueryKey,
  methods: readonly SavedPaymentMethodResponse[],
  generation: number,
  migrateQueryLocalBarrier = true,
): string {
  assertPaymentMethodDetachBarrierGenerationCurrent(generation);
  const connectedAccountId =
    connectedAccountIdFromMethods(methods) ??
    (() => {
      const persistedScope = readWalletAlias(paymentMethodsQueryKey);
      return persistedScope?.startsWith("wallet:")
        ? persistedScope.slice("wallet:".length)
        : null;
    })();
  if (connectedAccountId === null) {
    return queryBarrierScope(paymentMethodsQueryKey);
  }
  return registerWalletAlias(
    queryClient,
    paymentMethodsQueryKey,
    connectedAccountId,
    generation,
    migrateQueryLocalBarrier,
    !migrateQueryLocalBarrier,
  );
}

/**
 * Facility routes can alias the same organization-owned Stripe account.
 * Scope derivation is render-safe and side-effect free. Successful list
 * reconciliation and explicit mutation commits register aliases separately so
 * an interrupted React render cannot publish wallet state.
 */
export function paymentMethodBarrierScope(
  paymentMethodsQueryKey: QueryKey,
  methods: readonly SavedPaymentMethodResponse[],
): string {
  const connectedAccountId = connectedAccountIdFromMethods(methods);
  if (connectedAccountId !== null) {
    return walletBarrierScope(connectedAccountId);
  }
  return (
    readWalletAlias(paymentMethodsQueryKey) ??
    queryBarrierScope(paymentMethodsQueryKey)
  );
}

function detachedIdsQueryKey(barrierScope: string) {
  return ["payment-method-picker", "detached-ids", barrierScope] as const;
}

function uncertainIdsQueryKey(barrierScope: string) {
  return ["payment-method-picker", "uncertain-ids", barrierScope] as const;
}

function barrierIdsQueryKey(barrierScope: string, kind: BarrierKind) {
  return kind === "detached"
    ? detachedIdsQueryKey(barrierScope)
    : uncertainIdsQueryKey(barrierScope);
}

function readBarrierIds(
  queryClient: QueryClient,
  barrierScope: string,
  kind: BarrierKind,
): Set<string> {
  const ids = readPersistedIds(barrierScope, kind);
  const cached =
    queryClient.getQueryData<string[]>(
      barrierIdsQueryKey(barrierScope, kind),
    ) ?? [];
  for (const paymentMethodId of cached) ids.add(paymentMethodId);
  return ids;
}

function publishBarrierIds(
  queryClient: QueryClient,
  barrierScope: string,
  kind: BarrierKind,
  ids: Set<string>,
): boolean {
  const persisted = persistIds(barrierScope, ids, kind);
  queryClient.setQueryData(barrierIdsQueryKey(barrierScope, kind), [...ids]);
  return persisted;
}

function migrateBarrierIds(
  queryClient: QueryClient,
  sourceScope: string,
  targetScope: string,
  kind: BarrierKind,
  onlyIds?: ReadonlySet<string>,
): void {
  if (sourceScope === targetScope) return;
  const sourceIds = readBarrierIds(queryClient, sourceScope, kind);
  const movingIds = [...sourceIds].filter(
    (paymentMethodId) => onlyIds === undefined || onlyIds.has(paymentMethodId),
  );
  if (movingIds.length === 0) return;
  const targetIds = readBarrierIds(queryClient, targetScope, kind);
  const nextTargetIds = new Set(targetIds);
  for (const paymentMethodId of movingIds) {
    nextTargetIds.add(paymentMethodId);
  }
  if (!publishBarrierIds(queryClient, targetScope, kind, nextTargetIds)) return;

  const nextSourceIds = new Set(sourceIds);
  for (const paymentMethodId of movingIds) {
    nextSourceIds.delete(paymentMethodId);
  }
  // Once the target is durable, a failed source cleanup leaves only a safe
  // duplicate. Never remove the sole persisted fence before its target exists.
  publishBarrierIds(queryClient, sourceScope, kind, nextSourceIds);
}

function filterWalletAliases(
  queryClient: QueryClient,
  barrierScope: string,
  paymentMethodsQueryKey: QueryKey,
  blockedIds: ReadonlySet<string>,
): void {
  const aliases = walletQueryKeys.get(barrierScope);
  const connectedAccountId = barrierScope.startsWith("wallet:")
    ? barrierScope.slice("wallet:".length)
    : null;
  const queryKeys =
    aliases !== undefined
      ? aliases.values()
      : connectedAccountId === null
        ? [paymentMethodsQueryKey]
        : [];
  for (const queryKey of queryKeys) {
    // A tombstone protects an errored alias without laundering its retry state
    // back to success via setQueryData.
    if (queryClient.getQueryState(queryKey)?.status === "error") continue;
    queryClient.setQueryData<SavedPaymentMethodResponse[]>(queryKey, (old) =>
      old?.filter(
        (method) =>
          !blockedIds.has(method.payment_method_id) ||
          (connectedAccountId !== null &&
            connectedAccountId !== PLATFORM_WALLET_ID &&
            method.connect_account_id !== connectedAccountId),
      ),
    );
  }
}

export function detachedPaymentMethodIdsQueryKey(
  paymentMethodsQueryKey: QueryKey,
  methods: readonly SavedPaymentMethodResponse[] = [],
) {
  return detachedIdsQueryKey(
    paymentMethodBarrierScope(paymentMethodsQueryKey, methods),
  );
}

export function uncertainPaymentMethodIdsQueryKey(
  paymentMethodsQueryKey: QueryKey,
  methods: readonly SavedPaymentMethodResponse[] = [],
) {
  return uncertainIdsQueryKey(
    paymentMethodBarrierScope(paymentMethodsQueryKey, methods),
  );
}

export function removingPaymentMethodIdsQueryKey(
  paymentMethodsQueryKey: QueryKey,
  methods: readonly SavedPaymentMethodResponse[] = [],
) {
  return [
    "payment-method-picker",
    "removing-ids",
    paymentMethodBarrierScope(paymentMethodsQueryKey, methods),
  ] as const;
}

export function readKnownDetachedPaymentMethodIds(
  queryClient: QueryClient,
  paymentMethodsQueryKey: QueryKey,
  methods: readonly SavedPaymentMethodResponse[] = [],
): string[] {
  const barrierScope = paymentMethodBarrierScope(
    paymentMethodsQueryKey,
    methods,
  );
  try {
    return [...readBarrierIds(queryClient, barrierScope, "detached")];
  } catch (error) {
    if (error instanceof PaymentMethodRemovalPersistenceError) {
      return [...new Set(methods.map((method) => method.payment_method_id))];
    }
    throw error;
  }
}

export function readKnownUncertainPaymentMethodIds(
  queryClient: QueryClient,
  paymentMethodsQueryKey: QueryKey,
  methods: readonly SavedPaymentMethodResponse[] = [],
): string[] {
  const barrierScope = paymentMethodBarrierScope(
    paymentMethodsQueryKey,
    methods,
  );
  try {
    return [...readBarrierIds(queryClient, barrierScope, "uncertain")];
  } catch (error) {
    if (error instanceof PaymentMethodRemovalPersistenceError) {
      return [...new Set(methods.map((method) => method.payment_method_id))];
    }
    throw error;
  }
}

export function recordDetachedPaymentMethod(
  queryClient: QueryClient,
  paymentMethodsQueryKey: QueryKey,
  methods: readonly SavedPaymentMethodResponse[],
  paymentMethodId: string,
  generation: number,
  migrateQueryLocalBarrier = true,
): void {
  const barrierScope = registerPaymentMethodWalletAlias(
    queryClient,
    paymentMethodsQueryKey,
    methods,
    generation,
    migrateQueryLocalBarrier,
  );
  const detachedIds = readBarrierIds(queryClient, barrierScope, "detached");
  detachedIds.add(paymentMethodId);
  publishBarrierIds(queryClient, barrierScope, "detached", detachedIds);

  const uncertainIds = readBarrierIds(queryClient, barrierScope, "uncertain");
  uncertainIds.delete(paymentMethodId);
  publishBarrierIds(queryClient, barrierScope, "uncertain", uncertainIds);
  filterWalletAliases(
    queryClient,
    barrierScope,
    paymentMethodsQueryKey,
    new Set([...detachedIds, ...uncertainIds]),
  );
}

export function recordUncertainPaymentMethod(
  queryClient: QueryClient,
  paymentMethodsQueryKey: QueryKey,
  methods: readonly SavedPaymentMethodResponse[],
  paymentMethodId: string,
  generation: number,
  migrateQueryLocalBarrier = true,
): void {
  const barrierScope = registerPaymentMethodWalletAlias(
    queryClient,
    paymentMethodsQueryKey,
    methods,
    generation,
    migrateQueryLocalBarrier,
  );
  const uncertainIds = readBarrierIds(queryClient, barrierScope, "uncertain");
  uncertainIds.add(paymentMethodId);
  publishBarrierIds(queryClient, barrierScope, "uncertain", uncertainIds);
  const detachedIds = readBarrierIds(queryClient, barrierScope, "detached");
  filterWalletAliases(
    queryClient,
    barrierScope,
    paymentMethodsQueryKey,
    new Set([...detachedIds, ...uncertainIds]),
  );
}

export function beginPaymentMethodRemoval(
  queryClient: QueryClient,
  paymentMethodsQueryKey: QueryKey,
  methods: readonly SavedPaymentMethodResponse[],
  paymentMethodId: string,
  generation: number,
  migrateQueryLocalBarrier = true,
): PaymentMethodRemovalOperation | null {
  const connectedAccountId = connectedAccountIdFromMethods(methods);
  if (connectedAccountId === null) {
    throw new PaymentMethodRemovalPersistenceError();
  }
  const barrierScope = registerPaymentMethodWalletAlias(
    queryClient,
    paymentMethodsQueryKey,
    methods,
    generation,
    migrateQueryLocalBarrier,
  );
  if (!hasDurableOwner() || !hasDurableWalletAliases(paymentMethodsQueryKey)) {
    throw new PaymentMethodRemovalPersistenceError();
  }
  const queryKey = [
    "payment-method-picker",
    "removing-ids",
    barrierScope,
  ] as const;
  const removingIds = queryClient.getQueryData<string[]>(queryKey) ?? [];
  if (removingIds.length > 0) return null;
  // Persist the fail-closed fence before dispatch. A document reload can abort
  // the awaiting JavaScript continuation, making any in-flight DELETE outcome
  // inherently uncertain even when the network request eventually succeeds.
  const priorUncertainIds = readBarrierIds(
    queryClient,
    barrierScope,
    "uncertain",
  );
  const nextUncertainIds = new Set(priorUncertainIds);
  nextUncertainIds.add(paymentMethodId);
  if (
    !publishBarrierIds(queryClient, barrierScope, "uncertain", nextUncertainIds)
  ) {
    publishBarrierIds(
      queryClient,
      barrierScope,
      "uncertain",
      priorUncertainIds,
    );
    throw new PaymentMethodRemovalPersistenceError();
  }
  const detachedIds = readBarrierIds(queryClient, barrierScope, "detached");
  filterWalletAliases(
    queryClient,
    barrierScope,
    paymentMethodsQueryKey,
    new Set([...detachedIds, ...nextUncertainIds]),
  );
  queryClient.setQueryData<string[]>(queryKey, [paymentMethodId]);
  return Object.freeze({
    barrierScope,
    connectedAccountId,
    generation,
    paymentMethodId,
    paymentMethodsQueryKey,
    queryScope: paymentMethodQueryScope(paymentMethodsQueryKey),
  });
}

export function finishPaymentMethodRemoval(
  queryClient: QueryClient,
  operation: PaymentMethodRemovalOperation,
): void {
  assertPaymentMethodDetachBarrierGenerationCurrent(operation.generation);
  queryClient.setQueryData<string[]>(
    ["payment-method-picker", "removing-ids", operation.barrierScope],
    (old) => old?.filter((id) => id !== operation.paymentMethodId) ?? [],
  );
}

export function isPaymentMethodRemovalOperationCurrentForQuery(
  operation: PaymentMethodRemovalOperation,
): boolean {
  return (
    isPaymentMethodDetachBarrierGenerationCurrent(operation.generation) &&
    readWalletAliases(operation.paymentMethodsQueryKey).has(
      operation.barrierScope,
    )
  );
}

export function completePaymentMethodRemoval(
  queryClient: QueryClient,
  operation: PaymentMethodRemovalOperation,
): void {
  assertPaymentMethodDetachBarrierGenerationCurrent(operation.generation);
  const detachedIds = readBarrierIds(
    queryClient,
    operation.barrierScope,
    "detached",
  );
  detachedIds.add(operation.paymentMethodId);
  const detachedPersisted = publishBarrierIds(
    queryClient,
    operation.barrierScope,
    "detached",
    detachedIds,
  );
  const uncertainIds = readBarrierIds(
    queryClient,
    operation.barrierScope,
    "uncertain",
  );
  if (detachedPersisted) {
    uncertainIds.delete(operation.paymentMethodId);
    publishBarrierIds(
      queryClient,
      operation.barrierScope,
      "uncertain",
      uncertainIds,
    );
  }
  filterWalletAliases(
    queryClient,
    operation.barrierScope,
    operation.paymentMethodsQueryKey,
    new Set([...detachedIds, ...uncertainIds]),
  );
}

export function rejectPaymentMethodRemoval(
  queryClient: QueryClient,
  operation: PaymentMethodRemovalOperation,
): boolean {
  assertPaymentMethodDetachBarrierGenerationCurrent(operation.generation);
  const uncertainIds = readBarrierIds(
    queryClient,
    operation.barrierScope,
    "uncertain",
  );
  const priorUncertainIds = new Set(uncertainIds);
  uncertainIds.delete(operation.paymentMethodId);
  const cleared = publishBarrierIds(
    queryClient,
    operation.barrierScope,
    "uncertain",
    uncertainIds,
  );
  if (!cleared) {
    publishBarrierIds(
      queryClient,
      operation.barrierScope,
      "uncertain",
      priorUncertainIds,
    );
  }
  return cleared;
}

export function isDefinitivePaymentMethodRemovalFailure(
  error: unknown,
): boolean {
  if (
    error instanceof PaymentMethodRemovalInProgressError ||
    error instanceof PaymentMethodRemovalPersistenceError ||
    error instanceof PaymentMethodRemovalReferenceError
  ) {
    return true;
  }
  return (
    error instanceof Error &&
    "status" in error &&
    typeof error.status === "number" &&
    ([400, 404, 422].includes(error.status) ||
      (error.status === 409 &&
        "code" in error &&
        error.code === "PAYMENT_METHOD_IN_USE"))
  );
}

export async function invalidatePaymentMethodRemovalQueries(
  queryClient: QueryClient,
  operation: PaymentMethodRemovalOperation,
): Promise<void> {
  const aliases = [
    ...(walletQueryKeys.get(operation.barrierScope)?.values() ?? []),
  ];
  await Promise.all(
    aliases.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, exact: true }),
    ),
  );
  assertPaymentMethodDetachBarrierGenerationCurrent(operation.generation);

  const blockedIds = new Set([
    ...readBarrierIds(queryClient, operation.barrierScope, "detached"),
    ...readBarrierIds(queryClient, operation.barrierScope, "uncertain"),
  ]);
  filterWalletAliases(
    queryClient,
    operation.barrierScope,
    operation.paymentMethodsQueryKey,
    blockedIds,
  );
}

/**
 * A successful DELETE is stronger than every later unversioned list read.
 * Stripe-detached ids cannot be reused, so retain the fence for this
 * authenticated tab session; the auth boundary owns its eventual cleanup.
 */
export function reconcilePaymentMethods(
  queryClient: QueryClient,
  paymentMethodsQueryKey: QueryKey,
  methods: SavedPaymentMethodResponse[],
  requestGeneration: number,
): SavedPaymentMethodResponse[] {
  assertPaymentMethodDetachBarrierGenerationCurrent(requestGeneration);
  const connectedAccountIds = connectedAccountIdsFromMethods(methods);
  const queryScope = queryBarrierScope(paymentMethodsQueryKey);

  if (connectedAccountIds.size <= 1) {
    const barrierScope = registerPaymentMethodWalletAlias(
      queryClient,
      paymentMethodsQueryKey,
      methods,
      requestGeneration,
    );
    const detachedIds = readBarrierIds(queryClient, barrierScope, "detached");
    const uncertainIds = readBarrierIds(queryClient, barrierScope, "uncertain");
    publishBarrierIds(queryClient, barrierScope, "detached", detachedIds);
    publishBarrierIds(queryClient, barrierScope, "uncertain", uncertainIds);
    const blockedIds = new Set([...detachedIds, ...uncertainIds]);
    filterWalletAliases(
      queryClient,
      barrierScope,
      paymentMethodsQueryKey,
      blockedIds,
    );
    return methods.filter(
      (method) => !blockedIds.has(method.payment_method_id),
    );
  }

  // The player account route aggregates multiple organization wallets into
  // one query. Register that real query as an alias of every returned wallet,
  // while distributing any legacy query-local fence only when the card row
  // identifies one unambiguous connected account.
  registerWalletAliases(
    paymentMethodsQueryKey,
    connectedAccountIds,
    requestGeneration,
  );
  for (const kind of ["detached", "uncertain"] as const) {
    const queryIds = readBarrierIds(queryClient, queryScope, kind);
    const idsByAccount = new Map<string, Set<string>>();
    for (const paymentMethodId of queryIds) {
      const accounts = new Set(
        methods
          .filter((method) => method.payment_method_id === paymentMethodId)
          .map((method) => method.connect_account_id)
          .filter(
            (accountId): accountId is string =>
              typeof accountId === "string" && accountId.length > 0,
          ),
      );
      if (accounts.size !== 1) continue;
      const accountId = [...accounts][0];
      const ids = idsByAccount.get(accountId) ?? new Set<string>();
      ids.add(paymentMethodId);
      idsByAccount.set(accountId, ids);
    }
    for (const [accountId, ids] of idsByAccount) {
      migrateBarrierIds(
        queryClient,
        queryScope,
        walletBarrierScope(accountId),
        kind,
        ids,
      );
    }
  }

  const queryBlockedIds = new Set([
    ...readBarrierIds(queryClient, queryScope, "detached"),
    ...readBarrierIds(queryClient, queryScope, "uncertain"),
  ]);
  const blockedByWallet = new Map<string, Set<string>>();
  for (const connectedAccountId of connectedAccountIds) {
    const barrierScope = walletBarrierScope(connectedAccountId);
    const blockedIds = new Set([
      ...readBarrierIds(queryClient, barrierScope, "detached"),
      ...readBarrierIds(queryClient, barrierScope, "uncertain"),
    ]);
    blockedByWallet.set(connectedAccountId, blockedIds);
    filterWalletAliases(
      queryClient,
      barrierScope,
      paymentMethodsQueryKey,
      blockedIds,
    );
  }
  return methods.filter((method) => {
    if (queryBlockedIds.has(method.payment_method_id)) return false;
    const accountId = method.connect_account_id;
    return !(
      typeof accountId === "string" &&
      blockedByWallet.get(accountId)?.has(method.payment_method_id)
    );
  });
}
